import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Bell,
  BriefcaseBusiness,
  CircleUserRound,
  LayoutDashboard,
  Search,
  ShieldCheck,
  Sparkles,
} from "lucide-react";
import AppearanceToggle from "./AppearanceToggle";
import AdminShell from "./admin/AdminShell";
import EmployerShell from "./employer/EmployerShell";

const defaultNavItems = [
  { href: "/dashboard/admin", label: "Dashboard", icon: LayoutDashboard },
  { href: "/dashboard/applicant/bewerbung", label: "Bewerbung", icon: CircleUserRound },
  { href: "/dashboard/applicant/sprachtest-b1", label: "Sprachtest B1", icon: Sparkles },
  { href: "/stb/job-marketplace", label: "Jobs", icon: BriefcaseBusiness },
  { href: "/dashboard/security", label: "Security", icon: ShieldCheck },
];

const adminRoutePrefixes = [
  "/dashboard/admin",
  "/dashboard/users",
  "/dashboard/security",
  "/dashboard/systemstatus",
  "/dashboard/newsticker",
  "/dashboard/nachrichten",
  "/dashboard/chatbot-admin",
  "/dashboard/faq-admin",
  "/dashboard/gpt",
  "/dashboard/gpt-maintainer",
  "/dashboard/gpt-docagent",
  "/dashboard/blog",
  "/admin/gpt-terminal",
];

const employerRoutePrefixes = [
  "/dashboard/employer",
  "/dashboard/stb",
];

function isAdminRoute(pathname) {
  return adminRoutePrefixes.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));
}

function isEmployerRoute(pathname) {
  return employerRoutePrefixes.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));
}

function renderIcon(icon) {
  if (!icon) {
    return <LayoutDashboard size={18} strokeWidth={2} />;
  }

  if (typeof icon === "function" || (typeof icon === "object" && icon !== null)) {
    return React.createElement(icon, { size: 18, strokeWidth: 2 });
  }

  if (React.isValidElement(icon)) {
    return icon;
  }

  return <span className="dashboard-nav__emoji">{icon}</span>;
}

const CockpitShell = ({
  title,
  subtitle,
  badge = "Experts4Europe",
  navItems = defaultNavItems,
  actions = null,
  wideLayout = false,
  shellClassName = "",
  layoutClassName = "",
  pageClassName = "",
  contentClassName = "",
  theme = "default",
  shellLabel = "Arbeitsbereich",
  shellDescription = "Ein fokussierter Kontrollraum fuer deine naechsten Schritte, Signale und Entscheidungen.",
  modeLabel = "Mission Control",
  liveLabel = "Workspace",
  utilityHref = "/dashboard/account",
  utilityLabel = "Konto",
  topSignal = "Experts4Europe",
  topContext = "Dashboard System",
  notifications = [],
  showRailPanel = true,
  children,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const shellWidthClass = [wideLayout ? "dashboard-layout-wide" : "", layoutClassName].filter(Boolean).join(" ");

  if (theme === "admin" || isAdminRoute(location.pathname)) {
    return (
      <AdminShell
        title={title}
        subtitle={subtitle}
        badge={badge}
        actions={actions}
        shellClassName={shellClassName}
        shellLabel={shellLabel}
        shellDescription={shellDescription}
        modeLabel={modeLabel}
        liveLabel={liveLabel}
        utilityHref={utilityHref}
        utilityLabel={utilityLabel}
        topSignal={topSignal}
        topContext={topContext}
        notifications={notifications}
        pageClassName={pageClassName}
        contentClassName={contentClassName}
      >
        {children}
      </AdminShell>
    );
  }

  if (theme === "employer" || isEmployerRoute(location.pathname)) {
    return (
      <EmployerShell
        title={title}
        subtitle={subtitle}
        badge={badge}
        navItems={navItems}
        actions={actions}
        shellLabel={shellLabel}
        shellDescription={shellDescription}
        modeLabel={modeLabel}
        liveLabel={liveLabel}
        utilityHref={utilityHref}
        utilityLabel={utilityLabel}
        topSignal={topSignal}
        topContext={topContext}
        notifications={notifications}
        pageClassName={pageClassName}
        contentClassName={contentClassName}
      >
        {children}
      </EmployerShell>
    );
  }

  return (
    <div className={`dashboard-shell dashboard-tone-${theme}`}>
      <div className={`dashboard-layout ${shellWidthClass}`}>
        <aside className="dashboard-rail">
          <div className="dashboard-brand">
            <div className="dashboard-brand__mark" aria-hidden="true">
              <span>E4E</span>
            </div>
            <div className="dashboard-brand__copy">
              <p className="dashboard-brand__name">Experts4Europe</p>
              <p className="dashboard-brand__meta">{shellLabel}</p>
            </div>
          </div>

          {showRailPanel ? (
            <div className="dashboard-rail__panel">
              <div className="dashboard-rail__eyebrow">
                <span className="dashboard-kicker">{liveLabel}</span>
                <span className="dashboard-pill dashboard-pill-muted">{modeLabel}</span>
              </div>
              <p className="dashboard-rail__title">{badge}</p>
              <p className="dashboard-rail__text">{shellDescription}</p>
            </div>
          ) : null}

          <nav className="dashboard-nav" aria-label="Hauptnavigation">
            {navItems.map((item) => {
              const isActive =
                typeof item.match === "function"
                  ? item.match(location)
                  : location.pathname === item.href || location.pathname.startsWith(item.href.split("?")[0]);

              return (
                <button
                  key={item.href}
                  type="button"
                  onClick={() => navigate(item.href)}
                  className={`dashboard-nav__item${isActive ? " is-active" : ""}`}
                >
                  <span className="dashboard-nav__icon">{renderIcon(item.icon)}</span>
                  <span>{item.label}</span>
                </button>
              );
            })}
          </nav>

          <div className="dashboard-rail__footer">
            {utilityHref ? (
              <button
                type="button"
                className="dashboard-secondary-button"
                onClick={() => navigate(utilityHref)}
              >
                <CircleUserRound size={16} strokeWidth={2} />
                <span>{utilityLabel}</span>
              </button>
            ) : null}
            <button
              type="button"
              className="dashboard-secondary-button"
              onClick={() => navigate("/")}
            >
              <ArrowLeft size={16} strokeWidth={2} />
              <span>Zur Website</span>
            </button>
          </div>
        </aside>

        <main className={`dashboard-page ${pageClassName}`.trim()}>
          <div className="dashboard-topbar">
            <div className="dashboard-topbar__section">
              <span className="dashboard-topbar__label">{topSignal}</span>
              <span className="dashboard-topbar__value">{topContext}</span>
            </div>

            <div className="dashboard-topbar__section dashboard-topbar__section-center">
              <span className="dashboard-topbar__label">Fokus</span>
              <span className="dashboard-topbar__value">{title}</span>
            </div>

            <div className="dashboard-topbar__actions">
              <span className="dashboard-icon-button" aria-hidden="true">
                <Search size={15} strokeWidth={2} />
              </span>
              <span className="dashboard-icon-button" aria-hidden="true">
                <Bell size={15} strokeWidth={2} />
              </span>
              <AppearanceToggle compact />
            </div>
          </div>

          <header className="dashboard-hero">
            <div className="dashboard-hero__copy">
              <div className="dashboard-hero__meta">
                <span className="dashboard-kicker">{shellLabel}</span>
                <span className="dashboard-pill">{liveLabel}</span>
              </div>
              <h1 className="dashboard-hero__title">{title}</h1>
              {subtitle ? <p className="dashboard-hero__subtitle">{subtitle}</p> : null}
            </div>

            {actions ? <div className="dashboard-hero__actions">{actions}</div> : null}
          </header>

          <section className={`dashboard-content ${contentClassName}`.trim()}>{children}</section>
        </main>
      </div>
    </div>
  );
};

export default CockpitShell;
